import { decodePosition, packVec3 } from "@dust/world/internal";
import IWorldAbi from "@dust/world/out/IWorld.sol/IWorld.abi";
import { resourceToHex } from "@latticexyz/common";
import { decodeTransactionError } from "@permutation-city/common";
import {
  ActionButton,
  BarLoader,
  Card,
  useHotkeys,
} from "@permutation-city/ui";
import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { useDustClient } from "../../hooks/useDustClient";

const spawnRadius = 5;
const spawnTile =
  "0x03000001f900000095fffff9d200000000000000000000000000000000000000";

function getSpawnCoord(
  spawnTileCoord: readonly [number, number, number],
): [number, number, number] {
  const dx = Math.round(Math.random() * (spawnRadius * 2)) - spawnRadius;
  const dz = Math.round(Math.random() * (spawnRadius * 2)) - spawnRadius;
  const dy = dx === 0 && dz === 0 ? 1 : 0;
  return [
    spawnTileCoord[0] + dx,
    spawnTileCoord[1] + dy,
    spawnTileCoord[2] + dz,
  ];
}

export function Home() {
  const { data: dustClient } = useDustClient();
  const userAddress = dustClient?.appContext.userAddress;

  const spawnMutation = useMutation({
    mutationFn: async () => {
      if (!dustClient) {
        throw new Error("Could not connect to Dust client");
      }
      if (!userAddress) {
        throw new Error("User not found");
      }

      const spawnTileCoord = decodePosition(spawnTile);
      const spawnCoord = packVec3(getSpawnCoord(spawnTileCoord));
      const spawnEnergy = (817600000000000000n * 3n) / 10n;

      const result = await dustClient.provider.request({
        method: "systemCall",
        params: [
          {
            systemId: resourceToHex({
              type: "system",
              namespace: "",
              name: "SpawnSystem",
            }),
            abi: IWorldAbi,
            functionName: "spawn",
            args: [spawnTile, spawnCoord, spawnEnergy, "0x"],
          },
        ],
      });

      const errorMessage = decodeTransactionError(IWorldAbi, result);
      if (errorMessage) {
        throw new Error(errorMessage);
      }
      return result;
    },
  });

  const handleSpawn = useCallback(() => {
    spawnMutation.mutate();
  }, [spawnMutation]);

  useHotkeys("ctrl + E", () => handleSpawn());

  return (
    <Card>
      <div className="flex flex-col items-center justify-start w-full max-w-full sm:max-w-sm mx-auto space-y-6 uppercase">
        <div className="space-y-4 leading-relaxed">
          <p>
            Permutation City offers structure, shelter, and a shared mission: to
            explore the future of human coordination, governance, and economy in
            an autonomous world.
          </p>

          <p>
            Earn Permutation Pesos by contributing to the city&apos;s force
            field and completing the city&apos;s construction. Spend Permutation
            Pesos in the public marketplace or to open your own shop.
          </p>

          <p>
            The first spawn in Permutation City is free. Every subsequent spawn
            costs 50 Permutation Pesos, to offset the spawn&apos;s energy cost.
          </p>
        </div>

        <div className="w-full flex justify-between">
          <ActionButton hotkey="^+E" onClick={handleSpawn} disabled={false}>
            Enter Permutation City
          </ActionButton>
        </div>

        <div className="w-full flex justify-start">
          {spawnMutation.isPending && <BarLoader intervalRate={100} />}
          {spawnMutation.isSuccess && (
            <p className="uppercase text-success text-right">Success!</p>
          )}
          {spawnMutation.isError && (
            <p className="uppercase text-danger text-right">
              {spawnMutation.error?.message || "Something went wrong"}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
